import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Servicio de integración con SAP (Business Partner / Creditotal) — individual.
 * Habla con kredit-api-MT (/api/sap). El token de sesión lo inyecta el
 * interceptor HTTP global (mismo patrón que el resto de servicios).
 *
 * Endpoints:
 *   analyze -> valida sin tocar SAP (para "Analizar cliente" / auto-analizador)
 *   create  -> crea en SAP y guarda FE.C
 *   modify  -> modifica en SAP y guarda FE.M
 */
@Injectable({ providedIn: 'root' })
export class SapService {
  private baseUrl: string = (environment as any).BASE_LA;

  constructor(private http: HttpClient) {}

  analyze(code: string): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${this.baseUrl}/api/sap/person/${code}/analyze`, {})
    );
  }

  create(code: string): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${this.baseUrl}/api/sap/person/${code}/create`, {})
    );
  }

  /**
   * Modifica el cliente en SAP. Si `payload` es null, el backend re-sincroniza
   * los datos actuales de la BD. Si se pasa un payload ({ d: {...} }), se usa ese
   * (los campos de identidad los fuerza el backend de todas formas).
   */
  modify(code: string, payload: any = null): Promise<any> {
    return lastValueFrom(
      this.http.patch<any>(`${this.baseUrl}/api/sap/person/${code}/modify`, payload || {})
    );
  }

  /** Bitácora / traza de llamadas SAP para un cliente. */
  logs(code: string, limit: number = 50): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${this.baseUrl}/api/sap/person/${code}/logs?limit=${limit}`)
    );
  }

  /** Configuración dinámica del endpoint SAP. */
  getConfig(): Promise<any> {
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/sap/config`));
  }

  updateConfig(patch: any): Promise<any> {
    return lastValueFrom(this.http.put<any>(`${this.baseUrl}/api/sap/config`, patch));
  }

  // ===== Centro de control (módulo en Parámetros de Sistema) =====

  /** KPIs del Resumen. refresh=true fuerza recalcular el universo. */
  getKpis(refresh: boolean = false): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${this.baseUrl}/api/sap/dashboard/kpis${refresh ? '?refresh=true' : ''}`)
    );
  }

  /** Tabla maestra de clientes. */
  listClients(opts: { status?: string; search?: string; page?: number; limit?: number; error_tipo?: string } = {}): Promise<any> {
    const p = new URLSearchParams();
    p.set('status', opts.status || 'creados');
    if (opts.search) p.set('search', opts.search);
    if (opts.error_tipo) p.set('error_tipo', opts.error_tipo);
    p.set('page', String(opts.page || 1));
    p.set('limit', String(opts.limit || 25));
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/sap/clients?${p.toString()}`));
  }

  /** Bitácora global (todas las llamadas SAP). */
  allLogs(opts: { action?: string; limit?: number } = {}): Promise<any> {
    const p = new URLSearchParams();
    if (opts.action) p.set('action', opts.action);
    p.set('limit', String(opts.limit || 100));
    return lastValueFrom(this.http.get<any>(`${this.baseUrl}/api/sap/logs?${p.toString()}`));
  }

  /**
   * Descarga el reporte de clientes creados en SAP (xlsx) y dispara el "save"
   * del navegador. Sin fechas => TODOS los creados (botón "Exportar todos").
   * @param initDate 'YYYY-MM-DD' (opcional)
   * @param endDate  'YYYY-MM-DD' (opcional)
   */
  downloadCreatedReport(initDate?: string, endDate?: string): Promise<void> {
    return this.downloadReport('created', 'Clientes_Creados_SAP', initDate, endDate);
  }

  /**
   * Descarga el reporte de clientes con error en SAP (xlsx). El rango filtra por el
   * día del error; sin fechas => todo el historial.
   */
  downloadErrorsReport(initDate?: string, endDate?: string): Promise<void> {
    return this.downloadReport('errors', 'Clientes_Con_Error_SAP', initDate, endDate);
  }

  private async downloadReport(tipo: 'created' | 'errors', prefijo: string, initDate?: string, endDate?: string): Promise<void> {
    const p = new URLSearchParams();
    if (initDate) p.set('initDate', initDate);
    if (endDate) p.set('endDate', endDate);
    const qs = p.toString();
    const blob = await lastValueFrom(
      this.http.get(`${this.baseUrl}/api/sap/report/${tipo}${qs ? '?' + qs : ''}`, { responseType: 'blob' })
    );
    const label = (initDate && endDate) ? `${initDate}_a_${endDate}` : (initDate ? `desde_${initDate}` : (endDate ? `hasta_${endDate}` : 'TODOS'));
    const url = window.URL.createObjectURL(blob as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${prefijo}_${label}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }
}
